import { supabase } from "@/integrations/supabase/client";
import { safeLocalStorage } from "@/lib/safeStorage";

/** Cached REST/function responses the service worker replays when offline. */
const DATA_CACHE = "produce-data-v5";

/**
 * Sign out in a way that also works when the backend is unreachable.
 *
 * `supabase.auth.signOut()` posts to `/auth/v1/logout` first and only clears
 * local storage if that call succeeds — a network failure makes it return
 * early with the tokens still in place. That is exactly the situation this is
 * used in (the "no connection" screen), so a plain sign-out there would leave
 * the user looking at a logged-out page with a live session and a valid
 * refresh token on disk: the next person on that laptop is signed in as them.
 *
 * `scope: "local"` because the server round-trip can't succeed anyway; the
 * explicit storage + cache wipe is what actually guarantees the tokens are
 * gone. A refresh token stays valid server-side until it expires — acceptable
 * here, and the same trade-off `endExpiredSession` already makes.
 */
export async function forceSignOut(): Promise<void> {
  try {
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) throw error;
  } catch {
    safeLocalStorage.clear();
  }

  // Someone else's schedule must not come back out of the offline cache.
  try {
    await caches?.delete(DATA_CACHE);
  } catch {
    // No Cache API (or blocked): nothing cached to clear.
  }
}
